import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api.ts';

/**
 * The state behind every list screen: which page, how sorted, what filtered.
 *
 * Owned here rather than in each screen so that twelve tables cannot drift into
 * twelve slightly different behaviours -- and so the export link is built from
 * exactly the same query as the table, which is what stops a download quietly
 * disagreeing with what is on screen.
 */

export interface ListState {
  page: number;
  pageSize: number;
  sort?: string;
  dir: 'asc' | 'desc';
  search: string;
  filters: Record<string, string>;
}

export interface ListResult<T> {
  rows: T[];
  total: number;
  loading: boolean;
  error: string;
  state: ListState;
  /** Page count, at least 1 so the footer never reads "page 1 of 0". */
  pages: number;
  set: (patch: Partial<ListState>) => void;
  setFilter: (key: string, value: string) => void;
  clearFilters: () => void;
  reload: () => Promise<void>;
  /** The same query as a string, for building an export link. */
  qs: (extra?: Record<string, string>) => string;
}

function buildQs(s: ListState, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  p.set('page', String(s.page));
  p.set('pageSize', String(s.pageSize));
  if (s.sort) { p.set('sort', s.sort); p.set('dir', s.dir); }
  if (s.search.trim()) p.set('search', s.search.trim());
  for (const [k, v] of Object.entries(s.filters)) {
    if (v.trim()) p.set(`filter.${k}`, v.trim());
  }
  for (const [k, v] of Object.entries(extra)) p.set(k, v);
  return p.toString();
}

export function useList<T>(
  path: string,
  key: string,
  initial: Partial<ListState> = {},
): ListResult<T> {
  const [state, setState] = useState<ListState>({
    page: 1, pageSize: 25, dir: 'asc', search: '', filters: {}, ...initial,
  });
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const qs = useCallback(
    (extra?: Record<string, string>) => buildQs(state, extra), [state]);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await api.get<Record<string, unknown>>(`${path}?${buildQs(state)}`);
      setRows((res[key] as T[]) ?? []);
      setTotal(Number(res.total ?? 0));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the list');
    } finally {
      setLoading(false);
    }
  }, [path, key, state]);

  // Debounced: typing in a search or filter box must not fire a request per
  // keystroke, and paging should still feel immediate.
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const set = useCallback((patch: Partial<ListState>) => {
    setState((prev) => ({
      ...prev,
      ...patch,
      // Any change to what is being shown sends you back to page one.
      // Staying on page 7 of a list that now has two pages shows nothing and
      // reads as a broken screen.
      page: patch.page ?? 1,
    }));
  }, []);

  const setFilter = useCallback((k: string, v: string) => {
    setState((prev) => ({
      ...prev, page: 1, filters: { ...prev.filters, [k]: v },
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setState((prev) => ({ ...prev, page: 1, search: '', filters: {} }));
  }, []);

  const pages = Math.max(1, Math.ceil(total / state.pageSize));

  return useMemo(
    () => ({ rows, total, loading, error, state, pages, set, setFilter, clearFilters,
             reload: load, qs }),
    [rows, total, loading, error, state, pages, set, setFilter, clearFilters, load, qs],
  );
}
